import { AxiosError } from "axios";
import {
  CalendarClock,
  Check,
  ChevronDown,
  Edit3,
  Loader2,
  Search,
  Users,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  bulkUpdateUserSchedules,
  updateUserSchedules,
} from "../api/admin-panel";
import type { AdminPanelUser, UserSchedulePayload } from "../types/admin-panel";
import { CompanyRestDaysPanel } from "./CompanyRestDaysPanel";

type Props = {
  users: AdminPanelUser[];
  loading: boolean;
  onReload: () => Promise<void> | void;
};

type EditingMode =
  | { type: "single"; user: AdminPanelUser; schedules: UserSchedulePayload[] }
  | { type: "bulk"; userIds: number[]; schedules: UserSchedulePayload[] }
  | null;

const WEEK_DAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 7, label: "Domingo" },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function defaultFridayEnd() {
  const month = new Date().getMonth() + 1;
  return month === 7 || month === 8 ? "14:30" : "15:00";
}

function defaultSchedules(): UserSchedulePayload[] {
  const vigenteDesde = todayIso();

  return [1, 2, 3, 4, 5].map((day) => ({
    dia_semana: day,
    hora_inicio: "07:30",
    hora_fin: day === 5 ? defaultFridayEnd() : "15:30",
    tolerancia_minutos: 15,
    activo: true,
    vigente_desde: vigenteDesde,
    vigente_hasta: null,
  }));
}

function cloneSchedules(schedules?: UserSchedulePayload[]) {
  return schedules && schedules.length > 0
    ? schedules
        .map((schedule) => ({
          ...schedule,
          hora_inicio: schedule.hora_inicio.slice(0, 5),
          hora_fin: schedule.hora_fin.slice(0, 5),
          vigente_hasta: schedule.vigente_hasta ?? null,
        }))
        .sort((a, b) => a.dia_semana - b.dia_semana)
    : defaultSchedules();
}

export function SchedulesPanel({ users, loading, onReload }: Props) {
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("all");
  const [scheduleStatus, setScheduleStatus] = useState("all");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [editing, setEditing] = useState<EditingMode>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    tone: "success" | "error";
    text: string;
  } | null>(null);

  const departmentOptions = useMemo(
    () =>
      Array.from(
        new Set(
          users
            .map((user) => user.departamento)
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [users],
  );

  const filteredUsers = useMemo(() => {
    const value = search.trim().toLowerCase();

    return users.filter((user) => {
      const matchesSearch =
        !value ||
        user.nombre.toLowerCase().includes(value) ||
        user.correo.toLowerCase().includes(value);

      const matchesDepartment =
        department === "all" || user.departamento === department;

      const hasSchedule = (user.schedules ?? []).length > 0;
      const matchesScheduleStatus =
        scheduleStatus === "all" ||
        (scheduleStatus === "with" && hasSchedule) ||
        (scheduleStatus === "without" && !hasSchedule);

      return matchesSearch && matchesDepartment && matchesScheduleStatus;
    });
  }, [department, scheduleStatus, search, users]);

  const allVisibleSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((user) => selectedIds.includes(user.id));

  const toggleSelection = (userId: number) => {
    setSelectedIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId],
    );
  };

  const toggleAllVisible = () => {
    setSelectedIds((current) => {
      if (allVisibleSelected) {
        const visibleIds = new Set(filteredUsers.map((user) => user.id));
        return current.filter((id) => !visibleIds.has(id));
      }

      return Array.from(
        new Set([...current, ...filteredUsers.map((user) => user.id)]),
      );
    });
  };

  const openSingleEditor = (user: AdminPanelUser) => {
    setFeedback(null);
    setEditing({
      type: "single",
      user,
      schedules: cloneSchedules(user.schedules),
    });
  };

  const openBulkEditor = () => {
    if (selectedIds.length === 0) return;

    const firstSelected = users.find((user) => selectedIds.includes(user.id));

    setFeedback(null);
    setEditing({
      type: "bulk",
      userIds: selectedIds,
      schedules: cloneSchedules(firstSelected?.schedules),
    });
  };

  const closeEditor = () => {
    if (saving) return;
    setEditing(null);
  };

  const saveSchedules = async () => {
    if (!editing) return;

    setSaving(true);
    setFeedback(null);

    try {
      if (editing.type === "single") {
        await updateUserSchedules(editing.user.id, {
          schedules: editing.schedules,
        });
      } else {
        await bulkUpdateUserSchedules({
          user_ids: editing.userIds,
          schedules: editing.schedules,
        });
      }

      await onReload();
      setFeedback({
        tone: "success",
        text:
          editing.type === "single"
            ? "Horario actualizado correctamente."
            : `Horario aplicado a ${editing.userIds.length} usuario(s).`,
      });
      setEditing(null);
      setSelectedIds([]);
    } catch (error) {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            "No se pudo guardar el horario.")
          : "No se pudo guardar el horario.";

      setFeedback({
        tone: "error",
        text: message,
      });
    } finally {
      setSaving(false);
    }
  };

  const updateEditingSchedules = (schedules: UserSchedulePayload[]) => {
    setEditing((current) =>
      current
        ? {
            ...current,
            schedules,
          }
        : current,
    );
  };

  return (
    <div className="scroll-reveal-right relative flex h-full min-h-[720px] flex-col gap-5">
      <section className="dashboard-scale-in group relative overflow-hidden rounded-[32px] border border-[#e7ecef] bg-white/90 p-6 shadow-[0_14px_45px_rgba(15,23,42,.055)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-emerald-100 hover:shadow-[0_24px_70px_rgba(15,23,42,.08)]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -right-24 top-[-80px] h-[260px] w-[260px] rounded-full bg-emerald-300/22 blur-3xl transition duration-500 group-hover:scale-110" />
          <div className="absolute bottom-[-120px] left-[-70px] h-[240px] w-[240px] rounded-full bg-teal-300/14 blur-3xl transition duration-500 group-hover:scale-110" />
        </div>

        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.07)]">
              <CalendarClock size={14} />
              Gestión de horarios
            </div>

            <h3 className="mt-4 text-3xl font-black tracking-tight text-slate-950">
              Horarios del equipo
            </h3>

            <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
              Consulta, filtra y actualiza los horarios laborales por empleado. Puedes editar un usuario o aplicar una misma configuración a varios usuarios seleccionados.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 sm:min-w-[420px]">
            <MetricBox label="Usuarios" value={users.length} />
            <MetricBox
              label="Con horario"
              value={users.filter((user) => (user.schedules ?? []).length > 0).length}
            />
            <MetricBox label="Seleccionados" value={selectedIds.length} />
          </div>
        </div>
      </section>

      {feedback && <FeedbackBox feedback={feedback} />}

      <section className="dashboard-scale-in min-h-0 flex-1 overflow-hidden rounded-[32px] border border-[#e7ecef] bg-white/90 shadow-[0_14px_45px_rgba(15,23,42,.055)] backdrop-blur-xl transition-all duration-300 hover:border-emerald-100 hover:shadow-[0_22px_60px_rgba(15,23,42,.075)]">
        <div className="flex flex-col gap-4 border-b border-[#edf1f3] px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_10px_24px_rgba(16,185,129,.08)]">
              <Users size={19} />
            </div>

            <div>
              <h3 className="text-lg font-black text-slate-950">
                Listado de horarios
              </h3>

              <p className="text-sm font-semibold text-slate-500">
                {filteredUsers.length} usuario(s) visibles.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 rounded-2xl border border-[#e7ecef] bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-[0_8px_18px_rgba(15,23,42,.03)]">
              <Search size={16} className="text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar usuario..."
                className="min-w-[210px] bg-transparent outline-none placeholder:text-slate-400"
              />
            </div>

            <select
              value={department}
              onChange={(event) => setDepartment(event.target.value)}
              className="rounded-2xl border border-[#e7ecef] bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-[0_8px_18px_rgba(15,23,42,.03)]"
            >
              <option value="all">Todos los departamentos</option>
              {departmentOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              value={scheduleStatus}
              onChange={(event) => setScheduleStatus(event.target.value)}
              className="rounded-2xl border border-[#e7ecef] bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-[0_8px_18px_rgba(15,23,42,.03)]"
            >
              <option value="all">Todos los estados</option>
              <option value="with">Con horario</option>
              <option value="without">Sin horario</option>
            </select>

            <button
              type="button"
              onClick={openBulkEditor}
              disabled={selectedIds.length === 0}
              className="inline-flex items-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-4 py-2 text-sm font-black text-white shadow-[0_14px_32px_rgba(16,185,129,.20)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_38px_rgba(16,185,129,.26)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Edit3 size={15} />
              Editar seleccionados
            </button>
          </div>
        </div>

        <div className="max-h-[720px] overflow-auto">
          <table className="table-modern table-dark min-w-[1120px]">
            <thead>
              <tr>
                <th className="w-[64px]">
                  <button
                    type="button"
                    onClick={toggleAllVisible}
                    className={[
                      "grid h-8 w-8 place-items-center rounded-xl border text-xs font-black transition",
                      allVisibleSelected
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 bg-white text-slate-400 hover:border-emerald-200 hover:text-emerald-700",
                    ].join(" ")}
                  >
                    {allVisibleSelected ? <Check size={15} /> : "—"}
                  </button>
                </th>
                <th>Empleado</th>
                <th>Departamento</th>
                <th>Horario</th>
                <th>Vigencia</th>
                <th className="text-right">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <SkeletonRows />
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <div className="text-base font-black text-slate-950">
                      No hay usuarios para los filtros seleccionados.
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-500">
                      Ajusta la búsqueda o limpia los filtros.
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const schedules = cloneSchedules(user.schedules);
                  const isSelected = selectedIds.includes(user.id);

                  return (
                    <tr key={user.id} className="group">
                      <td>
                        <button
                          type="button"
                          onClick={() => toggleSelection(user.id)}
                          className={[
                            "grid h-9 w-9 place-items-center rounded-xl border transition",
                            isSelected
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 bg-white text-slate-400 hover:border-emerald-200 hover:text-emerald-700",
                          ].join(" ")}
                        >
                          {isSelected ? <Check size={16} /> : null}
                        </button>
                      </td>

                      <td>
                        <div className="flex items-center gap-3">
                          <div className="grid h-11 w-11 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-sm font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.06)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
                            {getInitials(user.nombre)}
                          </div>

                          <div>
                            <div className="font-black text-slate-950">
                              {user.nombre}
                            </div>

                            <div className="text-xs font-semibold text-slate-500">
                              {user.correo}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-black text-slate-500">
                          {user.departamento ?? "Sin departamento"}
                        </span>
                      </td>

                      <td>
                        <div className="flex max-w-[460px] flex-wrap gap-2">
                          {schedules.length === 0 ? (
                            <span className="text-sm font-bold text-slate-400">
                              Sin horario
                            </span>
                          ) : (
                            scheduleRangeBadges(schedules).map((badge) => (
                              <span
                                key={`${user.id}-${badge.label}-${badge.hora_inicio}-${badge.hora_fin}-${badge.activo}`}
                                className={[
                                  "inline-flex rounded-full border px-3 py-1 text-xs font-black",
                                  badge.activo
                                    ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                                    : "border-slate-200 bg-slate-50 text-slate-400",
                                ].join(" ")}
                              >
                                {badge.label} · {badge.hora_inicio}-{badge.hora_fin}
                                {!badge.activo ? " · Inactivo" : ""}
                              </span>
                            ))
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="text-sm font-semibold text-slate-500">
                          {formatVigencia(schedules)}
                        </div>
                      </td>

                      <td>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => openSingleEditor(user)}
                            className="inline-flex items-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.06)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-100"
                          >
                            <Edit3 size={14} />
                            Editar horario
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <CompanyRestDaysPanel />

      {editing ? createPortal(
        <div className="fixed inset-0 z-[105] flex items-center justify-center overflow-hidden bg-slate-950/30 p-4 backdrop-blur-md">
          <div className="dashboard-scale-in flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-[38px] border border-[#d7e5df] bg-white shadow-[0_44px_120px_rgba(15,23,42,.22)]">
            <div className="shrink-0 border-b border-[#edf1f3] bg-[linear-gradient(135deg,#ffffff,#f4fbf8)] px-7 py-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="grid h-14 w-14 place-items-center rounded-3xl border border-emerald-100 bg-emerald-50 text-emerald-700 shadow-[0_12px_28px_rgba(16,185,129,.10)]">
                    <CalendarClock size={24} />
                  </div>

                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
                      {editing.type === "single"
                        ? "Edición individual"
                        : "Edición masiva"}
                    </div>

                    <h3 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
                      {editing.type === "single"
                        ? `Horario de ${editing.user.nombre}`
                        : `Aplicar horario a ${editing.userIds.length} usuario(s)`}
                    </h3>

                    <p className="mt-1 text-sm font-semibold text-slate-500">
                      El nuevo horario sustituirá la configuración actual de los usuarios afectados.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closeEditor}
                  disabled={saving}
                  className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-60"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-7">
              <ScheduleEditor
                schedules={editing.schedules}
                onChange={updateEditingSchedules}
              />
            </div>

            <div className="shrink-0 flex flex-col gap-3 border-t border-[#edf1f3] bg-slate-50/80 px-7 py-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="text-sm font-semibold text-slate-500">
                {editing.type === "bulk"
                  ? "Revisa el horario antes de aplicarlo a todos los usuarios seleccionados."
                  : "Revisa el horario antes de guardarlo."}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeEditor}
                  disabled={saving}
                  className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-60"
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={() => void saveSchedules()}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-5 py-3 text-sm font-black text-white shadow-[0_14px_32px_rgba(16,185,129,.20)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_42px_rgba(16,185,129,.26)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Check size={16} />
                  )}
                  Guardar horario
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

function ScheduleEditor({
  schedules,
  onChange,
}: {
  schedules: UserSchedulePayload[];
  onChange: (schedules: UserSchedulePayload[]) => void;
}) {
  const usedDays = schedules.map((schedule) => schedule.dia_semana);

  const globalVigenteDesde = useMemo(() => {
    if (schedules.length === 0) return todayIso();

    const firstValue = schedules[0]?.vigente_desde ?? todayIso();
    const allSame = schedules.every(
      (schedule) => (schedule.vigente_desde ?? "") === firstValue,
    );

    return allSame ? firstValue : "";
  }, [schedules]);

  const globalVigenteHasta = useMemo(() => {
    if (schedules.length === 0) return "";

    const firstValue = schedules[0]?.vigente_hasta ?? "";
    const allSame = schedules.every(
      (schedule) => (schedule.vigente_hasta ?? "") === firstValue,
    );

    return allSame ? firstValue : "";
  }, [schedules]);

  const globalHoraInicio = useMemo(() => {
    if (schedules.length === 0) return "07:30";

    const firstValue = schedules[0]?.hora_inicio?.slice(0, 5) ?? "07:30";
    const allSame = schedules.every(
      (schedule) => schedule.hora_inicio.slice(0, 5) === firstValue,
    );

    return allSame ? firstValue : "";
  }, [schedules]);

  const globalHoraFin = useMemo(() => {
    if (schedules.length === 0) return "15:30";

    const firstValue = schedules[0]?.hora_fin?.slice(0, 5) ?? "15:30";
    const allSame = schedules.every(
      (schedule) => schedule.hora_fin.slice(0, 5) === firstValue,
    );

    return allSame ? firstValue : "";
  }, [schedules]);

  const allSchedulesActive =
    schedules.length > 0 && schedules.every((schedule) => schedule.activo);

  const updateSchedule = (
    index: number,
    patch: Partial<UserSchedulePayload>,
  ) => {
    onChange(
      schedules.map((schedule, currentIndex) =>
        currentIndex === index
          ? {
              ...schedule,
              ...patch,
            }
          : schedule,
      ),
    );
  };

  const updateGlobalVigencia = (
    patch: Pick<Partial<UserSchedulePayload>, "vigente_desde" | "vigente_hasta">,
  ) => {
    onChange(
      schedules.map((schedule) => ({
        ...schedule,
        ...patch,
      })),
    );
  };

  const updateGlobalTimes = (
    patch: Pick<Partial<UserSchedulePayload>, "hora_inicio" | "hora_fin">,
  ) => {
    onChange(
      schedules.map((schedule) => ({
        ...schedule,
        ...patch,
      })),
    );
  };

  const toggleGlobalActive = () => {
    const nextActive = !allSchedulesActive;

    onChange(
      schedules.map((schedule) => ({
        ...schedule,
        activo: nextActive,
      })),
    );
  };

  const addSchedule = () => {
    const nextDay = WEEK_DAYS.find((day) => !usedDays.includes(day.value));

    if (!nextDay) return;

    onChange([
      ...schedules,
      {
        dia_semana: nextDay.value,
        hora_inicio: "07:30",
        hora_fin: nextDay.value === 5 ? defaultFridayEnd() : "15:30",
        tolerancia_minutos: 15,
        activo: true,
        vigente_desde: globalVigenteDesde || todayIso(),
        vigente_hasta: globalVigenteHasta || null,
      },
    ]);
  };

  const removeSchedule = (index: number) => {
    onChange(schedules.filter((_, currentIndex) => currentIndex !== index));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
        <CalendarClock size={18} className="mt-0.5 shrink-0" />
        <div>
          Por defecto se propone 07:30-15:30 de lunes a jueves y 07:30-15:00 los viernes. Los meses de julio y agosto los viernes son de 7:30-14:30. Puedes ajustar cada día por empleado.
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <Field
          label="Entrada global"
          type="time"
          value={globalHoraInicio}
          onChange={(value) =>
            updateGlobalTimes({
              hora_inicio: value || "07:30",
            })
          }
        />

        <Field
          label="Salida global"
          type="time"
          value={globalHoraFin}
          onChange={(value) =>
            updateGlobalTimes({
              hora_fin: value || "15:30",
            })
          }
        />

        <Field
          label="Vigente desde global"
          type="date"
          value={globalVigenteDesde}
          onChange={(value) =>
            updateGlobalVigencia({
              vigente_desde: value || todayIso(),
            })
          }
        />

        <Field
          label="Vigente hasta global"
          type="date"
          value={globalVigenteHasta}
          onChange={(value) =>
            updateGlobalVigencia({
              vigente_hasta: value || null,
            })
          }
        />

        <label className="flex items-end gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-black text-slate-600">
          <input
            type="checkbox"
            checked={allSchedulesActive}
            onChange={toggleGlobalActive}
            className="h-4 w-4 accent-emerald-500"
          />
          Horario completo activo
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={addSchedule}
          disabled={schedules.length >= 7}
          className="inline-flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700 transition-all hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronDown size={14} />
          Añadir día
        </button>
      </div>

      <div className="max-h-[560px] space-y-3 overflow-y-auto pr-1">
        {schedules.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm font-bold text-slate-400">
            Este usuario no tiene horario configurado.
          </div>
        ) : (
          schedules
            .slice()
            .sort((a, b) => a.dia_semana - b.dia_semana)
            .map((schedule) => {
              const originalIndex = schedules.findIndex(
                (item) => item.dia_semana === schedule.dia_semana,
              );

              return (
                <div
                  key={schedule.dia_semana}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_8px_18px_rgba(15,23,42,.025)]"
                >
                  <div className="grid gap-3 md:grid-cols-[1.2fr_1fr_1fr_1fr_auto]">
                    <label className="block">
                      <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                        Día
                      </span>

                      <select
                        value={schedule.dia_semana}
                        onChange={(event) =>
                          updateSchedule(originalIndex, {
                            dia_semana: Number(event.target.value),
                          })
                        }
                        className="field-control"
                      >
                        {WEEK_DAYS.map((day) => (
                          <option
                            key={day.value}
                            value={day.value}
                            disabled={
                              day.value !== schedule.dia_semana &&
                              usedDays.includes(day.value)
                            }
                          >
                            {day.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <Field
                      label="Entrada"
                      type="time"
                      value={schedule.hora_inicio.slice(0, 5)}
                      onChange={(value) =>
                        updateSchedule(originalIndex, {
                          hora_inicio: value,
                        })
                      }
                    />

                    <Field
                      label="Salida"
                      type="time"
                      value={schedule.hora_fin.slice(0, 5)}
                      onChange={(value) =>
                        updateSchedule(originalIndex, {
                          hora_fin: value,
                        })
                      }
                    />

                    <Field
                      label="Tolerancia"
                      type="number"
                      value={String(schedule.tolerancia_minutos)}
                      onChange={(value) =>
                        updateSchedule(originalIndex, {
                          tolerancia_minutos: Number(value || 0),
                        })
                      }
                    />

                    <div className="flex items-end justify-end">
                      <button
                        type="button"
                        onClick={() => removeSchedule(originalIndex)}
                        className="grid h-11 w-11 place-items-center rounded-2xl border border-rose-100 bg-rose-50 text-rose-600 transition-all hover:bg-rose-100"
                        title="Eliminar horario"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <Field
                      label="Vigente desde"
                      type="date"
                      value={schedule.vigente_desde}
                      onChange={(value) =>
                        updateSchedule(originalIndex, {
                          vigente_desde: value || todayIso(),
                        })
                      }
                    />

                    <Field
                      label="Vigente hasta"
                      type="date"
                      value={schedule.vigente_hasta ?? ""}
                      onChange={(value) =>
                        updateSchedule(originalIndex, {
                          vigente_hasta: value || null,
                        })
                      }
                    />

                    <label className="flex items-end gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-black text-slate-600">
                      <input
                        type="checkbox"
                        checked={schedule.activo}
                        onChange={(event) =>
                          updateSchedule(originalIndex, {
                            activo: event.target.checked,
                          })
                        }
                        className="h-4 w-4 accent-emerald-500"
                      />
                      Activo
                    </label>
                  </div>
                </div>
              );
            })
        )}
      </div>
    </div>
  );
}

function MetricBox({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-[24px] border border-emerald-100 bg-white/80 px-4 py-3 shadow-[0_8px_22px_rgba(16,185,129,.06)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:bg-emerald-50 hover:shadow-[0_14px_30px_rgba(16,185,129,.10)]">
      <div className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">
        {label}
      </div>

      <div className="mt-1 text-2xl font-black text-slate-950">{value}</div>
    </div>
  );
}

function FeedbackBox({
  feedback,
}: {
  feedback: { tone: "success" | "error"; text: string };
}) {
  return (
    <div
      className={`rounded-[24px] border px-4 py-3 text-sm font-bold ${
        feedback.tone === "success"
          ? "border-emerald-100 bg-emerald-50 text-emerald-700"
          : "border-rose-100 bg-rose-50 text-rose-600"
      }`}
    >
      {feedback.text}
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, row) => (
        <tr key={row}>
          {Array.from({ length: 6 }).map((__, col) => (
            <td key={col}>
              <div className="h-5 w-24 animate-pulse rounded-xl bg-slate-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
        {label}
      </span>

      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="field-control"
      />
    </label>
  );
}

function dayLabel(value: number) {
  return WEEK_DAYS.find((day) => day.value === value)?.label ?? String(value);
}

type ScheduleRangeBadge = {
  label: string;
  hora_inicio: string;
  hora_fin: string;
  activo: boolean;
};

function scheduleRangeBadges(
  schedules: UserSchedulePayload[],
): ScheduleRangeBadge[] {
  const sorted = schedules
    .slice()
    .sort((a, b) => a.dia_semana - b.dia_semana);

  const groups: Array<{
    startDay: number;
    endDay: number;
    hora_inicio: string;
    hora_fin: string;
    activo: boolean;
  }> = [];

  sorted.forEach((schedule) => {
    const horaInicio = schedule.hora_inicio.slice(0, 5);
    const horaFin = schedule.hora_fin.slice(0, 5);
    const previous = groups[groups.length - 1];

    if (
      previous &&
      previous.endDay + 1 === schedule.dia_semana &&
      previous.hora_inicio === horaInicio &&
      previous.hora_fin === horaFin &&
      previous.activo === schedule.activo
    ) {
      previous.endDay = schedule.dia_semana;
      return;
    }

    groups.push({
      startDay: schedule.dia_semana,
      endDay: schedule.dia_semana,
      hora_inicio: horaInicio,
      hora_fin: horaFin,
      activo: schedule.activo,
    });
  });

  return groups.map((group) => ({
    label:
      group.startDay === group.endDay
        ? dayLabel(group.startDay)
        : `${dayLabel(group.startDay)}-${dayLabel(group.endDay)}`,
    hora_inicio: group.hora_inicio,
    hora_fin: group.hora_fin,
    activo: group.activo,
  }));
}

function formatVigencia(schedules: UserSchedulePayload[]) {
  if (schedules.length === 0) return "Sin vigencia";

  const desde = schedules
    .map((schedule) => schedule.vigente_desde)
    .filter(Boolean)
    .sort()[0];

  const hastaValues = schedules
    .map((schedule) => schedule.vigente_hasta)
    .filter((value): value is string => Boolean(value))
    .sort();

  const hasta = hastaValues[hastaValues.length - 1];

  return `${desde ? formatDate(desde) : "Sin inicio"} - ${
    hasta ? formatDate(hasta) : "Sin fin"
  }`;
}

function formatDate(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("es-ES");
}

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}
